// src/services/followService.ts

import { EntryModel } from "../models";
import { API_BASE_URL } from "../constants";
import { FollowUserModel } from "../models/FollowModel";
import { mapRawEntry } from "./entryMapper";


export interface FollowListResponse {
  content: FollowUserModel[];
  currentPage: number;
  totalPages: number;
  totalItems: number;
}

export interface FeedResponse {
  content: EntryModel[];
  currentPage: number;
  totalPages: number;
  totalItems: number;
}

const authHeaders = (): Record<string, string> => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// ─── Follow / Unfollow ──────────────────────────────────────────────────────

/**
 * Follow a user by their ID.
 * Throws on network error or non-2xx response.
 */
export const followUser = async (targetUserId: number): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/api/follow/${targetUserId}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
  });
  if (!response.ok && response.status !== 204) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || "Follow failed");
  }
};

/**
 * Unfollow a user by their ID.
 * Throws on network error or non-2xx response.
 */
export const unfollowUser = async (targetUserId: number): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/api/follow/${targetUserId}`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json", ...authHeaders() },
  });
  if (!response.ok && response.status !== 204) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || "Unfollow failed");
  }
};

// ─── Follower / Following Lists ─────────────────────────────────────────────

/**
 * Fetch paginated list of followers for a given profile user.
 */
export const fetchFollowers = async (
  userId: number,
  page: number = 0,
  size: number = 20
): Promise<FollowListResponse> => {
  const response = await fetch(
    `${API_BASE_URL}/api/follow/${userId}/followers?page=${page}&size=${size}`,
    { headers: { "Content-Type": "application/json", ...authHeaders() } }
  );
  if (!response.ok) throw new Error("Failed to fetch followers");
  const json = await response.json();
  return parseFollowListResponse(json);
};

/**
 * Fetch paginated list of users that the given profile user is following.
 */
export const fetchFollowing = async (
  userId: number,
  page: number = 0,
  size: number = 20
): Promise<FollowListResponse> => {
  const response = await fetch(
    `${API_BASE_URL}/api/follow/${userId}/following?page=${page}&size=${size}`,
    { headers: { "Content-Type": "application/json", ...authHeaders() } }
  );
  if (!response.ok) throw new Error("Failed to fetch following");
  const json = await response.json();
  return parseFollowListResponse(json);
};

// ─── Activity Feed ──────────────────────────────────────────────────────────

/**
 * Fetch the authenticated user's activity feed (entries from followed users).
 */
export const fetchFeed = async (
  page: number = 0,
  size: number = 20
): Promise<FeedResponse> => {
  const token = localStorage.getItem("token");
  if (!token) throw new Error("Authentication required");

  const response = await fetch(
    `${API_BASE_URL}/api/follow/feed?page=${page}&size=${size}`,
    { headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` } }
  );
  if (!response.ok) throw new Error("Failed to fetch feed");
  const json = await response.json();
  return parseFeedResponse(json);
};

// ─── Parse Helpers ──────────────────────────────────────────────────────────

const parseFollowListResponse = (json: any): FollowListResponse => ({
  content: (json.content ?? []).map((item: any): FollowUserModel => ({
    userId: item.userId,
    username: item.username,
    role: item.role,
    isFollowing: item.isFollowing ?? false,
    followedAt: item.followedAt,
  })),
  currentPage: json.currentPage ?? 0,
  totalPages: json.totalPages ?? 0,
  totalItems: json.totalItems ?? 0,
});

const parseFeedResponse = (json: any): FeedResponse => ({
  content: (json.content ?? []).map(mapRawEntry),
  currentPage: json.currentPage ?? 0,
  totalPages: json.totalPages ?? 0,
  totalItems: json.totalItems ?? 0,
});